import React, { useState, useEffect, useRef, useMemo } from 'react';
import { api, formatCurrency, formatDate, expenseCategories, getCategoryLabel } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Upload,
  FileText,
  CheckCircle2,
  Loader2,
  ArrowRight,
  Check,
  X,
  Plus,
  CheckCheck,
  RotateCcw,
  Building2,
  Tag
} from 'lucide-react';

const allCategories = [
  { value: 'revenue', label: 'Revenue', group: 'Income' },
  ...expenseCategories,
  { value: 'investment', label: 'Investment', group: 'Finance' },
  { value: 'loan_emi', label: 'Loan EMI', group: 'Finance' },
];

export default function BankStatementUpload({ open, onOpenChange, ventures, locations, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [parseProgress, setParseProgress] = useState('');
  const [selectedVenture, setSelectedVenture] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [detectedBankAccount, setDetectedBankAccount] = useState(null);
  const [parsedTransactions, setParsedTransactions] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [accepted, setAccepted] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [newCatDialog, setNewCatDialog] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState('');
  const [pdfPassword, setPdfPassword] = useState('');
  const fileRef = useRef(null);

  const [reconciliation, setReconciliation] = useState(null);
  
  // New state for merchant filtering and bulk actions
  const [merchantFilter, setMerchantFilter] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'merchant', 'amount', 'category'
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [bulkCategoryDialog, setBulkCategoryDialog] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [saveAsRule, setSaveAsRule] = useState(true);
  const [bulkMerchant, setBulkMerchant] = useState(null);

  const filteredLocations = locations.filter(l => l.venture_id === selectedVenture);
  const mergedCategories = [...allCategories, ...customCategories.map(c => ({ ...c, group: 'Custom' }))];

  useEffect(() => {
    if (open) {
      api.get('/categories/custom').then(r => setCustomCategories(r.data)).catch(() => {});
      api.get('/bank-accounts').then(r => setBankAccounts(r.data)).catch(() => {});
    }
  }, [open]);

  // Compute unique merchants for filter dropdown
  const uniqueMerchants = useMemo(() => {
    const merchants = new Map();
    parsedTransactions.forEach(tx => {
      const nm = tx.normalized_merchant || 'Unknown';
      if (!merchants.has(nm)) {
        merchants.set(nm, { name: nm, count: 0, total: 0 });
      }
      const m = merchants.get(nm);
      m.count += 1;
      m.total += tx.amount || 0;
    });
    // Filter out empty names and sort by count
    return Array.from(merchants.values())
      .filter(m => m.name && m.name.trim())
      .sort((a, b) => b.count - a.count);
  }, [parsedTransactions]);

  // Filter and sort transactions
  const displayedTransactions = useMemo(() => {
    // Add original index to each transaction for tracking
    let filtered = parsedTransactions.map((tx, idx) => ({ ...tx, _origIndex: idx }));
    if (merchantFilter) {
      filtered = filtered.filter(tx => tx.normalized_merchant === merchantFilter);
    }
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'date') cmp = (a.date || '').localeCompare(b.date || '');
      else if (sortBy === 'merchant') cmp = (a.normalized_merchant || '').localeCompare(b.normalized_merchant || '');
      else if (sortBy === 'amount') cmp = (a.amount || 0) - (b.amount || 0);
      else if (sortBy === 'category') cmp = (a.category || '').localeCompare(b.category || '');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [parsedTransactions, merchantFilter, sortBy, sortDir]);

  // Handle bulk category update for a merchant
  const handleBulkMerchantUpdate = async () => {
    if (!bulkMerchant || !bulkCategory) return;
    setLoading(true);
    try {
      const res = await api.post('/bank-statement/bulk-update-merchant', {
        batch_id: batchId,
        normalized_merchant: bulkMerchant,
        category: bulkCategory,
        save_rule: saveAsRule
      });
      if (res.data.transactions) {
        setParsedTransactions(res.data.transactions);
      }
      toast.success(`Updated ${res.data.updated_count} transactions for ${bulkMerchant}${saveAsRule ? ' (rule saved)' : ''}`);
      setBulkCategoryDialog(false);
      setBulkMerchant(null);
      setBulkCategory('');
      // Keep the filter but the transactions are now updated
    } catch (e) {
      toast.error('Failed to update');
      console.error('Bulk update error:', e);
    }
    setLoading(false);
  };

  // Handle selection toggle
  const toggleSelect = (idx) => {
    const newSet = new Set(selectedIndices);
    if (newSet.has(idx)) newSet.delete(idx);
    else newSet.add(idx);
    setSelectedIndices(newSet);
  };

  // Select all visible
  const selectAllVisible = () => {
    const newSet = new Set(selectedIndices);
    displayedTransactions.forEach((_, i) => newSet.add(i));
    setSelectedIndices(newSet);
  };

  // Clear selection
  const clearSelection = () => setSelectedIndices(new Set());

  const processChunks = async (batchId, totalChunks) => {
    for (let i = 0; i < totalChunks; i++) {
      setParseProgress(`Processing chunk ${i + 1} of ${totalChunks}...`);
      let retries = 2;
      while (retries > 0) {
        try {
          await api.post(`/bank-statement/process-chunk/${batchId}/${i}`, null, {
            timeout: 180000  // 3 minutes per chunk for AI processing
          });
          break; // success, move to next chunk
        } catch (err) {
          retries--;
          if (retries <= 0) throw err;
          setParseProgress(`Retrying chunk ${i + 1} of ${totalChunks}...`);
          await new Promise(r => setTimeout(r, 2000));
        }
      }
    }
    // Fetch final results after ALL chunks are done
    setParseProgress('Finalizing results...');
    const statusRes = await api.get(`/bank-statement/parse-status/${batchId}`, { timeout: 60000 });
    return statusRes.data;
  };

  const handleParse = async () => {
    if (files.length === 0) { toast.error('Please upload bank statement file(s)'); return; }
    if (!selectedVenture || !selectedLocation) { toast.error('Please select venture and location'); return; }
    if (!selectedBankAccount) { toast.error('Please select or add a bank account'); return; }

    setLoading(true);
    try {
      let allTransactions = [];
      let lastBatchId = '';
      let detectedAccount = null;
      let lastRecon = null;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext = file.name.split('.').pop().toLowerCase();
        const isStructured = ['csv', 'xlsx', 'xls'].includes(ext);

        setParseProgress(`${isStructured ? 'Parsing' : 'Uploading'} file ${i + 1} of ${files.length}: ${file.name}`);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('venture_id', selectedVenture);
        formData.append('location_id', selectedLocation);
        if (selectedBankAccount) formData.append('bank_account_id', selectedBankAccount);
        if (pdfPassword) formData.append('pdf_password', pdfPassword);

        if (isStructured) {
          // Use new structured ingestion pipeline (no AI)
          setParseProgress(`Parsing ${file.name} (structured)...`);
          const res = await api.post('/bank-statement/ingest', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000
          });

          if (res.data.status === 'ai_fallback') {
            // Structured parse failed — fall back to AI pipeline
            toast.info(`${file.name}: Falling back to AI parser`);
            setParseProgress(`AI analyzing ${file.name}...`);
            const aiRes = await api.post('/bank-statement/parse-file', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 60000
            });
            const { batch_id, total_chunks, bank_account } = aiRes.data;
            const result = await processChunks(batch_id, total_chunks);
            allTransactions = [...allTransactions, ...(result.transactions || [])];
            lastBatchId = batch_id;
            if (bank_account) detectedAccount = bank_account;
          } else {
            // Structured parse succeeded
            const txns = (res.data.transactions || []).map(tx => ({
              ...tx,
              confidence: tx.confidence?.overall >= 0.90 ? 'high' : tx.confidence?.overall >= 0.60 ? 'medium' : 'low',
              confidence_detail: tx.confidence,
              auto_matched: tx.confidence?.overall >= 0.90
            }));
            allTransactions = [...allTransactions, ...txns];
            lastBatchId = res.data.batch_id;
            lastRecon = res.data.reconciliation;

            const reconStatus = res.data.reconciliation?.status;
            if (reconStatus === 'pass') {
              toast.success(`${file.name}: ${txns.length} transactions parsed. Reconciliation passed.`);
            } else if (reconStatus === 'fail') {
              toast.error(`${file.name}: Reconciliation FAILED — review balances carefully`);
            } else {
              toast.info(`${file.name}: ${txns.length} transactions. Review recommended.`);
            }
          }
        } else {
          // PDF/image: Use existing AI chunked pipeline
          const res = await api.post('/bank-statement/parse-file', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000  // 3 minutes for PDF parsing with AI
          });

          if (!res?.data?.batch_id) {
            throw new Error('Upload failed - server did not return batch ID.');
          }
          
          const { batch_id, total_chunks, bank_account } = res.data;
          setParseProgress(`AI analyzing file ${i + 1} (${total_chunks} chunk${total_chunks > 1 ? 's' : ''})...`);
          const result = await processChunks(batch_id, total_chunks);
          allTransactions = [...allTransactions, ...(result.transactions || [])];
          lastBatchId = batch_id;
          if (bank_account) detectedAccount = bank_account;
        }
      }

      setParsedTransactions(allTransactions);
      setBatchId(lastBatchId);
      setReconciliation(lastRecon);
      if (detectedAccount) {
        setDetectedBankAccount(detectedAccount);
        if (detectedAccount.auto_detected && !selectedBankAccount) {
          toast.info(`Auto-detected: ${detectedAccount.bank_name} (${detectedAccount.account_number_masked})`);
        }
      }
      const initial = {};
      allTransactions.forEach((tx, i) => { initial[i] = tx.confidence === 'high' || tx.auto_matched === true; });
      setAccepted(initial);
      setStep(2);
      setParseProgress('');
      toast.success(`Parsed ${allTransactions.length} transactions from ${files.length} file(s)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || 'Failed to parse bank statement');
      setParseProgress('');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = async (index, newCategory) => {
    const tx = parsedTransactions[index];
    try {
      const res = await api.patch('/bank-statement/update-category', null, {
        params: { batch_id: batchId, transaction_date: tx.date, description: tx.description, new_category: newCategory }
      });
      // Update locally instead of replacing entire array (preserves local changes like type flips, biz/personal)
      const updated = [...parsedTransactions];
      const vendorKey = tx.vendor_key;
      const newAccepted = { ...accepted, [index]: true };
      
      updated[index] = { ...tx, category: newCategory, confidence: 'high' };
      
      // Also update similar vendor transactions locally
      if (vendorKey) {
        updated.forEach((t, i) => {
          if (t.vendor_key === vendorKey) {
            updated[i] = { ...t, category: newCategory, confidence: 'high' };
            newAccepted[i] = true;
          }
        });
      }
      
      setParsedTransactions(updated);
      setAccepted(newAccepted);
      if ((res.data.similar_updated || 0) > 1) toast.success(`Updated ${res.data.similar_updated} similar transactions`);
      
      // Learn correction for future imports
      api.post('/bank-statement/learn-correction', {
        narration: tx.description, category: newCategory, direction: tx.type
      }).catch(() => {});
    } catch { toast.error('Failed to update category'); }
  };

  const handleFlipType = async (index) => {
    const tx = parsedTransactions[index];
    const newType = tx.type === 'credit' ? 'debit' : 'credit';
    const newCategory = newType === 'credit' ? 'revenue' : (tx.category === 'revenue' ? 'other' : tx.category);
    try {
      await api.patch('/bank-statement/update-type', null, {
        params: { batch_id: batchId, transaction_date: tx.date, description: tx.description, new_type: newType, new_category: newCategory }
      });
      const updated = [...parsedTransactions];
      updated[index] = { ...tx, type: newType, category: newCategory };
      setParsedTransactions(updated);
      toast.success(`Changed to ${newType === 'credit' ? 'Credit (Revenue)' : 'Debit (Expense)'}`);
      
      // Learn direction correction for future imports
      api.post('/bank-statement/learn-correction', {
        narration: tx.description, direction: newType, category: newCategory
      }).catch(() => {});
    } catch { toast.error('Failed to update type'); }
  };

  const handleToggleBusiness = async (index) => {
    const tx = parsedTransactions[index];
    const newValue = !tx.is_business;
    const updated = [...parsedTransactions];
    updated[index] = { ...tx, is_business: newValue };
    setParsedTransactions(updated);
    // Save to DB so it persists
    try {
      await api.patch('/bank-statement/update-business', null, {
        params: { batch_id: batchId, transaction_date: tx.date, description: tx.description, is_business: newValue }
      });
    } catch { /* silent - local state already updated */ }
  };

  const acceptedCount = Object.values(accepted).filter(Boolean).length;

  const handleConfirm = async () => {
    const acceptedIndices = Object.entries(accepted).filter(([, v]) => v).map(([k]) => parseInt(k));
    if (acceptedIndices.length === 0) { toast.error('Accept at least one transaction'); return; }
    setLoading(true);
    try {
      const res = await api.post('/bank-statement/confirm', { batch_id: batchId, accepted_indices: acceptedIndices });
      let msg = res.data.message || `Imported ${res.data.imported} transactions`;
      
      // Show investment count if any
      if (res.data.investment_count > 0) {
        toast.success(`${res.data.investment_count} investment(s) added to your portfolio!`, { duration: 5000 });
      }
      
      toast.success(msg);
      setStep(3);
      setTimeout(() => { resetForm(); onOpenChange(false); if (onSuccess) onSuccess(); }, 2000);
    } catch { toast.error('Failed to import'); }
    finally { setLoading(false); }
  };

  const handleAddCustomCategory = async () => {
    if (!newCatLabel.trim()) return;
    const value = newCatLabel.trim().toLowerCase().replace(/\s+/g, '_');
    try {
      const res = await api.post('/categories/custom', { value, label: newCatLabel.trim() });
      setCustomCategories(prev => [...prev, res.data]);
      setNewCatLabel('');
      setNewCatDialog(false);
      toast.success('Custom category added');
    } catch { toast.error('Failed to add category'); }
  };

  const resetForm = () => {
    setStep(1); setFiles([]); setSelectedVenture(''); setSelectedLocation('');
    setSelectedBankAccount(''); setDetectedBankAccount(null);
    setParsedTransactions([]); setBatchId(''); setAccepted({}); setPdfPassword('');
    setParseProgress(''); setReconciliation(null);
    // Reset merchant filter state
    setMerchantFilter(''); setSortBy('date'); setSortDir('asc');
    setSelectedIndices(new Set()); setBulkCategoryDialog(false);
    setBulkCategory(''); setBulkMerchant(null);
  };

  const getConfidenceBadge = (confidence) => {
    const styles = { high: 'bg-green-100 text-green-700 border-green-200', medium: 'bg-amber-100 text-amber-700 border-amber-200', low: 'bg-red-100 text-red-700 border-red-200' };
    // Handle both string and object confidence
    const level = typeof confidence === 'string' ? confidence : 
      (confidence?.overall >= 0.90 ? 'high' : confidence?.overall >= 0.60 ? 'medium' : 'low');
    return styles[level] || styles.low;
  };
  
  const getConfidenceLabel = (confidence) => {
    // Handle both string and object confidence
    if (typeof confidence === 'string') return confidence;
    if (confidence?.overall >= 0.90) return 'high';
    if (confidence?.overall >= 0.60) return 'medium';
    return 'low';
  };
  
  const getCategoryDisplay = (cat) => mergedCategories.find(c => c.value === cat)?.label || cat;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto" data-testid="bank-statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-amber-500" />
            Bank Statement Import
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-center gap-4 py-3 border-b">
          {[{ n: 1, label: 'Upload' }, { n: 2, label: 'Review' }, { n: 3, label: 'Done' }].map((s, i) => (
            <React.Fragment key={s.n}>
              {i > 0 && <ArrowRight className="w-4 h-4 text-zinc-300" />}
              <div className={`flex items-center gap-2 ${step >= s.n ? (s.n === 3 ? 'text-green-600' : 'text-amber-600') : 'text-zinc-400'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${step >= s.n ? (s.n === 3 ? 'bg-green-100' : 'bg-amber-100') : 'bg-zinc-100'}`}>{s.n}</div>
                <span className="text-sm font-medium">{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Venture</Label><Select value={selectedVenture} onValueChange={(v) => { setSelectedVenture(v); setSelectedLocation(''); }}><SelectTrigger className="mt-1.5" data-testid="bs-venture-select"><SelectValue placeholder="Select venture" /></SelectTrigger><SelectContent>{ventures.map((v) => <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Location</Label><Select value={selectedLocation} onValueChange={setSelectedLocation} disabled={!selectedVenture}><SelectTrigger className="mt-1.5" data-testid="bs-location-select"><SelectValue placeholder="Select location" /></SelectTrigger><SelectContent>{filteredLocations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            
            {/* Bank Account Selection - Required */}
            <div>
              <Label className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-zinc-500" />
                Bank Account
              </Label>
              <Select value={selectedBankAccount || '__none__'} onValueChange={(v) => setSelectedBankAccount(v === '__none__' ? '' : v)}>
                <SelectTrigger className="mt-1.5" data-testid="bs-bank-account-select">
                  <SelectValue placeholder="Select bank account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" className="text-zinc-400">-- Select bank account --</SelectItem>
                  {bankAccounts.map((acc) => (
                    <SelectItem key={acc.bank_account_id} value={acc.bank_account_id}>
                      {acc.bank_name}{acc.label ? ` - ${acc.label}` : ''}{acc.account_number_masked ? ` (${acc.account_number_masked})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!selectedBankAccount && (
                <p className="text-xs text-amber-600 mt-1">Please select which bank account this statement belongs to</p>
              )}
            </div>
            
            <div>
              <Label>Bank Statement File(s)</Label>
              <p className="text-xs text-zinc-500 mt-1 mb-2">Upload one or more bank statements. AI will extract and categorize transactions from all files.</p>
              <div className="border-2 border-dashed border-zinc-200 rounded-xl p-8 text-center cursor-pointer hover:border-zinc-400 transition-colors" onClick={() => fileRef.current?.click()}>
                <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-zinc-700">{files.length > 0 ? `${files.length} file(s) selected` : 'Click to upload statements'}</p>
                <p className="text-xs text-zinc-400 mt-1">CSV, Excel, PDF, PNG, JPG up to 10MB each. CSV/Excel preferred for best accuracy.</p>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} data-testid="bs-file-input" />
              </div>
              {files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-zinc-50 rounded-md px-3 py-1.5 text-xs">
                      <span className="text-zinc-700 truncate">{f.name}</span>
                      <button onClick={(e) => { e.stopPropagation(); setFiles(prev => prev.filter((_, idx) => idx !== i)); }} className="text-zinc-400 hover:text-red-500 ml-2"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {files.some(f => f.name.toLowerCase().endsWith('.pdf')) && (
              <div>
                <Label>PDF Password <span className="text-zinc-400 font-normal">(if protected)</span></Label>
                <Input type="password" placeholder="Leave blank if not protected" value={pdfPassword} onChange={(e) => setPdfPassword(e.target.value)} className="mt-1.5" data-testid="bs-pdf-password" />
              </div>
            )}
            <Button onClick={handleParse} disabled={loading || files.length === 0 || !selectedBankAccount} className="w-full" data-testid="bs-parse-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{parseProgress || 'Parsing...'}</> : <><FileText className="w-4 h-4 mr-2" />Parse {files.length > 1 ? `${files.length} Statements` : 'Statement'}</>}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 py-3">
            {/* Reconciliation status */}
            {reconciliation && (
              <div className={`rounded-lg px-3 py-2 text-sm border ${
                reconciliation.status === 'pass' ? 'bg-green-50 border-green-200' :
                reconciliation.status === 'fail' ? 'bg-red-50 border-red-200' :
                'bg-amber-50 border-amber-200'
              }`} data-testid="reconciliation-banner">
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${
                    reconciliation.status === 'pass' ? 'text-green-800' :
                    reconciliation.status === 'fail' ? 'text-red-800' :
                    'text-amber-800'
                  }`}>
                    {reconciliation.status === 'pass' ? 'Reconciliation Passed' :
                     reconciliation.status === 'fail' ? 'Reconciliation Failed' :
                     'Reconciliation: Review Needed'}
                  </span>
                  <div className="text-xs space-x-3">
                    {reconciliation.opening_balance != null && <span>Opening: {formatCurrency(reconciliation.opening_balance)}</span>}
                    <span>Debits: {formatCurrency(reconciliation.total_debits)}</span>
                    <span>Credits: {formatCurrency(reconciliation.total_credits)}</span>
                    {reconciliation.actual_closing != null && <span>Closing: {formatCurrency(reconciliation.actual_closing)}</span>}
                    {reconciliation.closing_delta != null && reconciliation.closing_delta !== 0 && (
                      <span className="text-red-600 font-medium">Delta: {formatCurrency(Math.abs(reconciliation.closing_delta))}</span>
                    )}
                  </div>
                </div>
                {reconciliation.warnings?.length > 0 && (
                  <div className="mt-1 text-xs text-amber-700">
                    {reconciliation.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </div>
                )}
              </div>
            )}
            {/* Show detected bank account */}
            {detectedBankAccount && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
                <span className="font-medium text-blue-800">Bank Account:</span>
                <span className="text-blue-700 ml-1">{detectedBankAccount.bank_name} ({detectedBankAccount.account_number_masked})</span>
                {detectedBankAccount.auto_detected && <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">Auto-detected</span>}
              </div>
            )}
            
            {/* Merchant Filter & Bulk Actions Toolbar */}
            <div className="bg-zinc-50 border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* Merchant Filter */}
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <Label className="text-xs text-zinc-500 whitespace-nowrap">Filter by Merchant:</Label>
                  <Select value={merchantFilter || '__all__'} onValueChange={(v) => { setMerchantFilter(v === '__all__' ? '' : v); setSelectedIndices(new Set()); }}>
                    <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="merchant-filter">
                      <SelectValue placeholder="All Merchants" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Merchants ({parsedTransactions.length})</SelectItem>
                      {uniqueMerchants.map(m => (
                        <SelectItem key={m.name} value={m.name || 'unknown'}>
                          {m.name || 'Unknown'} ({m.count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Sort Controls */}
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-zinc-500">Sort:</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="h-8 text-xs w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="merchant">Merchant</SelectItem>
                      <SelectItem value="amount">Amount</SelectItem>
                      <SelectItem value="category">Category</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
                    {sortDir === 'asc' ? '↑' : '↓'}
                  </Button>
                </div>
                
                {/* Bulk Actions */}
                <div className="flex gap-2">
                  {merchantFilter && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-8 text-xs bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                      onClick={() => { setBulkMerchant(merchantFilter); setBulkCategoryDialog(true); }}
                      data-testid="bulk-update-merchant-btn"
                    >
                      <Tag className="w-3.5 h-3.5 mr-1" />
                      Update All "{merchantFilter}" ({displayedTransactions.length})
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Quick stats when filtered */}
              {merchantFilter && (
                <div className="flex items-center gap-4 text-xs text-zinc-600 pt-1 border-t">
                  <span>Showing <strong>{displayedTransactions.length}</strong> of {parsedTransactions.length} transactions</span>
                  <span>Total: <strong>{formatCurrency(displayedTransactions.reduce((s, t) => s + (t.amount || 0), 0))}</strong></span>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm flex-1 mr-3">
                <span className="font-medium text-amber-800">Review each transaction.</span>
                <span className="text-amber-600 ml-1">Accept or change category, then import.</span>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => { const all = {}; parsedTransactions.forEach((_, i) => { all[i] = true; }); setAccepted(all); }} data-testid="bs-accept-all"><CheckCheck className="w-3.5 h-3.5 mr-1" />All</Button>
                <Button variant="outline" size="sm" onClick={() => { const all = {}; parsedTransactions.forEach((_, i) => { all[i] = false; }); setAccepted(all); }}><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset</Button>
                <Button variant="outline" size="sm" onClick={() => setNewCatDialog(true)} data-testid="bs-add-category"><Plus className="w-3.5 h-3.5 mr-1" />Category</Button>
              </div>
            </div>
            <div className="max-h-[420px] overflow-auto border rounded-lg" data-testid="bs-transactions-list">
              <table className="w-full text-sm min-w-[750px]">
                <thead className="bg-zinc-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-2.5 font-medium text-xs w-24 cursor-pointer hover:bg-zinc-100" onClick={() => { setSortBy('date'); setSortDir(d => sortBy === 'date' ? (d === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Date {sortBy === 'date' && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left p-2.5 font-medium text-xs w-28 cursor-pointer hover:bg-zinc-100" onClick={() => { setSortBy('merchant'); setSortDir(d => sortBy === 'merchant' ? (d === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Merchant {sortBy === 'merchant' && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left p-2.5 font-medium text-xs">Description</th>
                    <th className="text-left p-2.5 font-medium text-xs w-32 cursor-pointer hover:bg-zinc-100" onClick={() => { setSortBy('category'); setSortDir(d => sortBy === 'category' ? (d === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Category {sortBy === 'category' && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-center p-2.5 font-medium text-xs w-14">Type</th>
                    <th className="text-right p-2.5 font-medium text-xs w-24 cursor-pointer hover:bg-zinc-100" onClick={() => { setSortBy('amount'); setSortDir(d => sortBy === 'amount' ? (d === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Amount {sortBy === 'amount' && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-center p-2.5 font-medium text-xs w-18">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedTransactions.map((tx, index) => {
                    // Use the original index we stored
                    const origIndex = tx._origIndex;
                    const isAccepted = accepted[origIndex] === true;
                    const isRejected = accepted[origIndex] === false;
                    const isInvestment = tx.category === 'investment';
                    const isTransfer = tx.category === 'inter_account_transfer';
                    const isLowConfidence = getConfidenceLabel(tx.confidence) === 'low';
                    return (
                      <tr key={origIndex} className={`border-b transition-colors ${isAccepted ? 'bg-green-50/50' : isRejected ? 'bg-red-50/30 opacity-60' : isTransfer ? 'bg-cyan-50/50' : isInvestment ? 'bg-purple-50/50' : isLowConfidence ? 'bg-amber-50/50' : ''}`}>
                        <td className="p-2.5 text-zinc-500 text-xs">{formatDate(tx.date)}</td>
                        <td className="p-2.5">
                          <button 
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline text-left truncate max-w-[100px] block"
                            onClick={() => { setMerchantFilter(tx.normalized_merchant || 'Unknown'); }}
                            title={`Filter by ${tx.normalized_merchant || 'Unknown'}`}
                          >
                            {tx.normalized_merchant || 'Unknown'}
                          </button>
                        </td>
                        <td className="p-2.5">
                          <div className="truncate max-w-[220px] text-xs" title={tx.description}>{tx.description}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full border ${getConfidenceBadge(tx.confidence)}`} title={tx.direction_reason || tx.confidence_detail ? `Direction: ${tx.direction_method || 'AI'} (${(tx.confidence_detail?.direction || 0) * 100}%)\nCategory: ${tx.category_source || 'AI'} (${(tx.confidence_detail?.category || 0) * 100}%)\n${tx.direction_reason || ''}` : ''}>{getConfidenceLabel(tx.confidence)}</span>
                            {tx.auto_matched && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">auto</span>}
                            {tx.group_count > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">{tx.group_count}x</span>}
                            {isInvestment && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">{tx.investment_type || 'investment'}</span>}
                            {isTransfer && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-700 border border-cyan-200">transfer</span>}
                          </div>
                        </td>
                        <td className="p-2.5"><Select value={tx.category} onValueChange={(v) => handleCategoryChange(origIndex, v)}><SelectTrigger className="h-8 text-xs"><SelectValue>{getCategoryDisplay(tx.category)}</SelectValue></SelectTrigger><SelectContent>{mergedCategories.map((cat) => <SelectItem key={cat.value} value={cat.value} className="text-xs">{cat.label}</SelectItem>)}</SelectContent></Select></td>
                        <td className="p-2.5 text-center"><button onClick={() => handleFlipType(origIndex)} className={`text-xs px-2 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${tx.type === 'credit' ? 'bg-green-50 text-green-600 border border-green-200 hover:bg-green-100' : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'}`} title="Click to flip Credit/Debit" data-testid={`flip-type-${origIndex}`}>{tx.type === 'credit' ? 'Cr' : 'Dr'}</button></td>
                        <td className={`p-2.5 text-right font-medium text-xs ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>{tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)}</td>
                        <td className="p-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setAccepted(p => ({ ...p, [origIndex]: true }))} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${isAccepted ? 'bg-green-500 text-white' : 'bg-zinc-100 text-zinc-500 hover:bg-green-100 hover:text-green-600'}`}><Check className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setAccepted(p => ({ ...p, [origIndex]: false }))} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${isRejected ? 'bg-red-500 text-white' : 'bg-zinc-100 text-zinc-500 hover:bg-red-100 hover:text-red-600'}`}><X className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <span className="text-sm text-zinc-500"><span className="font-medium text-green-600">{acceptedCount}</span> of {parsedTransactions.length} accepted</span>
              <Button onClick={handleConfirm} disabled={loading || acceptedCount === 0} data-testid="bs-confirm-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <><Check className="w-4 h-4 mr-2" />Import {acceptedCount}</>}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-green-600" /></div>
            <h3 className="text-xl font-semibold text-zinc-900 mb-2">Import Complete!</h3>
            <p className="text-zinc-500">{acceptedCount} transactions imported successfully.</p>
          </div>
        )}
      </DialogContent>

      <Dialog open={newCatDialog} onOpenChange={setNewCatDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>Add Custom Category</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Category Name</Label><Input value={newCatLabel} onChange={(e) => setNewCatLabel(e.target.value)} placeholder="e.g. Investment in Gold" className="mt-1.5" /></div>
            <Button onClick={handleAddCustomCategory} className="w-full"><Plus className="w-4 h-4 mr-2" />Add Category</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Category Update Dialog */}
      <Dialog open={bulkCategoryDialog} onOpenChange={setBulkCategoryDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-amber-500" />
              Update Category for "{bulkMerchant}"
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <p className="text-amber-800">
                This will update <strong>{displayedTransactions.length}</strong> transaction(s) matching merchant "<strong>{bulkMerchant}</strong>".
              </p>
            </div>
            
            <div>
              <Label className="text-sm">Select Category</Label>
              <Select value={bulkCategory || undefined} onValueChange={setBulkCategory}>
                <SelectTrigger className="mt-1.5" data-testid="bulk-category-select">
                  <SelectValue placeholder="Choose category..." />
                </SelectTrigger>
                <SelectContent>
                  {mergedCategories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-zinc-50 rounded-lg border">
              <input 
                type="checkbox" 
                id="saveRule" 
                checked={saveAsRule} 
                onChange={(e) => setSaveAsRule(e.target.checked)}
                className="w-4 h-4 rounded border-zinc-300"
              />
              <label htmlFor="saveRule" className="text-sm">
                <span className="font-medium">Save as rule for future imports</span>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Future transactions from "{bulkMerchant}" will be auto-categorized
                </p>
              </label>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setBulkCategoryDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleBulkMerchantUpdate} 
                disabled={!bulkCategory || loading}
                className="flex-1"
                data-testid="bulk-apply-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Apply to All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
